import React, { useContext, useMemo } from "react";

import qs from "query-string";

import { BrowserRouter as Router } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  QueryObserverOptions,
} from "@tanstack/react-query";

import useBoardStatus from "./hooks/useBoardStatus";

const isDev = process.env.NODE_ENV === "development";

const Status = React.memo(() => {
  useBoardStatus();

  return null;
});

interface AppContext {
  screenId: string;
  clientId: string;
  config: {
    theme: boolean;
    connect: boolean;
    preview: boolean;
  };
}

const AppContext = React.createContext<AppContext>(undefined!);

const { Provider } = AppContext;

export const useAppContext = () => useContext(AppContext);

export const useAppConfig = () => useAppContext().config;

const App = React.memo<{ children: React.ReactNode }>(({ children }) => {
  const basename = useMemo(() => {
    if (isDev) {
      return "/";
    }

    return window.location.pathname
      .split("/")
      .filter(Boolean)
      .slice(0, 4)
      .join("/");
  }, []);

  const value = useMemo((): AppContext => {
    const {
      location: { search, pathname },
    } = window;

    const query = qs.parse(search);

    const paths = pathname.split("/");

    const screenId = isDev ? process.env.LOCALE_BOARD_ID : paths[4];

    const clientId = isDev ? process.env.API_CLIENT_TOKEN : paths[2];

    return {
      screenId,
      clientId,
      config: {
        theme: !!query.theme,
        connect: !!query.connect,
        preview: !!query?.preview,
      },
    };
  }, []);

  const client = useMemo(() => {
    let options: QueryObserverOptions = {
      refetchInterval: 300000,
    };

    if (value?.config?.theme) {
      options = {
        staleTime: Infinity,
        cacheTime: Infinity,
        refetchInterval: undefined,
      };
    }

    const client = new QueryClient({
      defaultOptions: {
        queries: options,
      },
    });

    return client;
  }, []);

  return (
    <Provider value={value}>
      <QueryClientProvider client={client}>
        <Router basename={basename}>{children}</Router>
        <Status />
      </QueryClientProvider>
    </Provider>
  );
});

export default App;
